import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Calendar, User, MessageSquare } from 'lucide-react';
import { format } from 'date-fns';

interface InternalTaskBoardProps {
  teamId?: string;
  assignedToMe?: boolean;
  canCreate?: boolean;
  teamMembers?: { user_id: string; full_name: string }[];
}

const priorityColors: Record<string, string> = {
  low: 'bg-muted text-muted-foreground',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-destructive/20 text-destructive',
};

const columns = [
  { key: 'todo', label: 'To Do', color: 'border-muted-foreground/30' },
  { key: 'in_progress', label: 'In Progress', color: 'border-blue-400' },
  { key: 'done', label: 'Done', color: 'border-green-400' },
];

export function InternalTaskBoard({ teamId, assignedToMe = false, canCreate = false, teamMembers = [] }: InternalTaskBoardProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', description: '', priority: 'medium', assigned_to: '', due_date: '' });

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ['internal-tasks', teamId, assignedToMe],
    queryFn: async () => {
      let query = supabase.from('internal_tasks').select('*').neq('status', 'cancelled').order('created_at', { ascending: false });
      if (teamId) query = query.eq('team_id', teamId);
      if (assignedToMe && user) query = query.eq('assigned_to', user.id);
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
  });

  const createTask = useMutation({
    mutationFn: async () => {
      if (!teamId || !user) return;
      const { error } = await supabase.from('internal_tasks').insert({
        team_id: teamId,
        assigned_by: user.id,
        assigned_to: newTask.assigned_to || null,
        title: newTask.title,
        description: newTask.description || null,
        priority: newTask.priority as any,
        due_date: newTask.due_date || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['internal-tasks'] });
      setShowCreate(false);
      setNewTask({ title: '', description: '', priority: 'medium', assigned_to: '', due_date: '' });
      toast.success('Task created');
    },
    onError: () => toast.error('Failed to create task'),
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase.from('internal_tasks').update({ status: status as any }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['internal-tasks'] }),
  });

  if (isLoading) return <div className="text-center py-8 text-muted-foreground">Loading tasks...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Task Board</h3>
        {canCreate && teamId && (
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button size="sm"><Plus className="h-4 w-4 mr-1" /> New Task</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create Task</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input placeholder="Task title" value={newTask.title} onChange={(e) => setNewTask({ ...newTask, title: e.target.value })} />
                <Textarea placeholder="Description" value={newTask.description} onChange={(e) => setNewTask({ ...newTask, description: e.target.value })} />
                <div className="grid grid-cols-2 gap-3">
                  <Select value={newTask.priority} onValueChange={(v) => setNewTask({ ...newTask, priority: v })}>
                    <SelectTrigger><SelectValue placeholder="Priority" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input type="date" value={newTask.due_date} onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })} />
                </div>
                {teamMembers.length > 0 && (
                  <Select value={newTask.assigned_to} onValueChange={(v) => setNewTask({ ...newTask, assigned_to: v })}>
                    <SelectTrigger><SelectValue placeholder="Assign to..." /></SelectTrigger>
                    <SelectContent>
                      {teamMembers.map((m) => (
                        <SelectItem key={m.user_id} value={m.user_id}>{m.full_name || 'Unnamed'}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Button onClick={() => createTask.mutate()} disabled={!newTask.title || createTask.isPending} className="w-full">
                  {createTask.isPending ? 'Creating...' : 'Create Task'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {columns.map((col) => {
          const colTasks = tasks.filter((t: any) => t.status === col.key);
          return (
            <div key={col.key} className={`border-t-4 ${col.color} rounded-lg`}>
              <div className="p-3 bg-muted/30 rounded-t-lg">
                <h4 className="font-medium text-sm">{col.label} ({colTasks.length})</h4>
              </div>
              <div className="p-2 space-y-2 min-h-[200px]">
                {colTasks.map((task: any) => (
                  <Card key={task.id} className="shadow-sm">
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-sm font-medium">{task.title}</span>
                        <Badge className={`text-[10px] ${priorityColors[task.priority]}`}>{task.priority}</Badge>
                      </div>
                      {task.description && <p className="text-xs text-muted-foreground line-clamp-2">{task.description}</p>}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {task.due_date && (
                          <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{format(new Date(task.due_date), 'MMM d')}</span>
                        )}
                      </div>
                      <div className="flex gap-1">
                        {col.key !== 'todo' && (
                          <Button variant="ghost" size="sm" className="text-xs h-6 px-2"
                            onClick={() => updateStatus.mutate({ id: task.id, status: col.key === 'done' ? 'in_progress' : 'todo' })}>
                            ← {col.key === 'done' ? 'In Progress' : 'To Do'}
                          </Button>
                        )}
                        {col.key !== 'done' && (
                          <Button variant="ghost" size="sm" className="text-xs h-6 px-2"
                            onClick={() => updateStatus.mutate({ id: task.id, status: col.key === 'todo' ? 'in_progress' : 'done' })}>
                            {col.key === 'todo' ? 'In Progress' : 'Done'} →
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {colTasks.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No tasks</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
